<template>
  <nav aria-label="Primary navigation" class="navbar navbar-expand-lg navbar-dark" style="background-color:#003865;">
    <div class="container-fluid">
      <div @click="homeByLogo()" class="navbar-brand d-flex flex-wrap">
        <img src="../../assets/logo-reverse.png" alt="Minnesota Department of Health Logo" class="me-4 flex-shrink-0" style="height:38px;"/>
        <div class="text-white fs-4 text-nowrap">{{ appTitle }}</div>
      </div>
      <button v-if="authStore.authenticated && !authStore.loadingProfile" class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
        <span class="navbar-toggler-icon "></span>
      </button>
    </div>
    <div class="container-fluid">
      <div class="collapse navbar-collapse vstack gap-2" id="navbarSupportedContent">
        <div class="ms-auto">
          <div
            v-if="authStore.authenticated && adminMenuItems.length > 0"
            class="d-inline-flex"
          >
            <a
              href="#"
              class="text-white custom-hover-white mx-2"
              id="adminMenuLink"
              role="menuitem"
              aria-haspopup="true"
              aria-expanded="false"
              @click.prevent="toggleAdminMenu"
              @keydown.enter.prevent="toggleAdminMenu"
              @keydown.space.prevent="toggleAdminMenu"
            >
              Admin
              <i class="pi pi-chevron-down ms-1" style="font-size: 0.75rem; line-height: 1;"></i>
            </a>

            <TieredMenu
              ref="adminMenu"
              :model="adminMenuItems"
              popup
              :aria-labelledby="'adminMenuLink'"
            />
          </div>

          <div v-if="authStore.authenticated && !authStore.loadingProfile" class="d-inline-flex">
            <a href="#"
               id="userMenuLink"
               class="text-white custom-hover-white mx-2"
               aria-haspopup="true"
               aria-expanded="false"
               @click.prevent="toggleUserMenu"
               @keydown.enter.prevent="toggleUserMenu"
               @keydown.space.prevent="toggleUserMenu">
              {{ authStore.user?.username }}
              <i class="pi pi-chevron-down ms-1" style="font-size: 0.75rem; line-height: 1;"></i>
            </a>

            <TieredMenu ref="userMenu"
                        :model="userMenuItems"
                        popup
                        :aria-labelledby="'userMenuLink'" />
          </div>
        </div>
      </div>
    </div>
  </nav>
  <Popover ref="op">
    <div class="p-3 d-flex">
      <div class="me-4">
        <Avatar :label="authStore.user?.initials" size="large" shape="circle" />
      </div>
      <div>
        {{ authStore.user?.firstName }} {{ authStore.user?.lastName }}
        <br />
        <small>{{ authStore.user?.primaryOrganizationName }}</small>
      </div>
    </div>
  </Popover>
</template>

<script setup>
import {useAuthStore} from '@/stores/authStore';
import Popover from 'primevue/popover';
import Avatar from 'primevue/avatar';
import TieredMenu from 'primevue/tieredmenu';
import {computed, ref} from 'vue';
import router from "@/router/index.js";
import keycloakService from "@/services/keycloakService.js";

const op = ref();

const authStore = useAuthStore();
const appTitle = import.meta.env.VITE_APP_TITLE;

async function logout() {
  await keycloakService.CallLogout();
  authStore.authenticated = false;
}

function homeByLogo(){
  if(authStore.authenticated){
    router.push({ path: '/MainLandingPage' });
  } else {
    router.push({ path: '/' });
  }
}

// Admin Menu
const adminMenu = ref(null);
function toggleAdminMenu(event) {
  adminMenu.value.toggle(event);
}

// Section header style constant (DRY)
const SECTION_HEADER_STYLE = {
  'font-weight': '800',
  'font-size': '1rem',
  'color': 'rgb(0, 56, 101)',
  'padding': '0.25rem',
  'background-color': '#eeeeee',
  'cursor': 'default',
  'text-decoration': 'none !important',
  'opacity': '1'
};

const adminMenuItems = computed(() => {
  const routes = router.getRoutes().filter(r => {
    if (!r.path.startsWith('/Admin') || r.path === '/Admin') {
      return false;
    }

    // If route has requiresRoles, check if user has any of the required roles
    if (r.meta?.requiresRoles) {
      return r.meta.requiresRoles.some(role => authStore.hasRole(role));
    }

    // If no requiresRoles specified, only show to admin users
    return authStore.isAdminUser;
  });

  // Categorize routes by area based on their requiresRoles metadata
  // ADMIN users should see all routes, non-admin users only see routes for their specific role
  const networkRoutes = routes.filter(r =>
    r.meta?.requiresRoles?.includes('NetworkAdequacy') &&
    (authStore.hasRole('ADMIN') || authStore.hasRole('NetworkAdequacy'))
  );

  const investigatorRoutes = routes.filter(r =>
    r.meta?.requiresRoles?.includes('Investigator') &&
    (authStore.hasRole('ADMIN') || authStore.hasRole('Investigator'))
  );

  const adminOnlyRoutes = routes.filter(r =>
    !r.meta?.requiresRoles?.includes('NetworkAdequacy') &&
    !r.meta?.requiresRoles?.includes('Investigator') &&
    authStore.hasRole('ADMIN')
  );

  const menuItems = [];

  // Helper function to create section header
  const createSectionHeader = (label, icon) => ({
    label,
    icon,
    disabled: true,
    class: 'menu-section-header',
    style: SECTION_HEADER_STYLE
  });

  // Helper function to create menu item
  const createMenuItem = (route) => ({
    label: route.meta?.title || route.name || route.path,
    icon: 'pi pi-angle-right',
    command: () => router.push(route.path)
  });

  // Add Network Adequacy section
  if (networkRoutes.length > 0) {
    menuItems.push(createSectionHeader('Network Adequacy', 'pi pi-sitemap'));
    networkRoutes.forEach(r => menuItems.push(createMenuItem(r)));
  }

  // Add separator if we have both sections
  if (networkRoutes.length > 0 && investigatorRoutes.length > 0) {
    menuItems.push({ separator: true });
  }

  // Add Investigator section
  if (investigatorRoutes.length > 0) {
    menuItems.push(createSectionHeader('Investigator', 'pi pi-search'));
    investigatorRoutes.forEach(r => menuItems.push(createMenuItem(r)));
  }

  // Add separator if we have admin-only items
  if ((networkRoutes.length > 0 || investigatorRoutes.length > 0) && adminOnlyRoutes.length > 0) {
    menuItems.push({ separator: true });
  }

  // Add Admin-only section
  if (adminOnlyRoutes.length > 0) {
    menuItems.push(createSectionHeader('Administration', 'pi pi-cog'));
    adminOnlyRoutes.forEach(r => menuItems.push(createMenuItem(r)));
  }

  return menuItems;
});

// User Menu
const userMenu = ref(null);
function toggleUserMenu(event) {
  userMenu.value.toggle(event);
}

const userMenuItems = [
  {
    label: 'Manage Account',
    icon: 'pi pi-user-edit',
    command: () => router.push('/ManageAccount'),
  },
  { separator: true },
  {
    label: 'Sign Out',
    icon: 'pi pi-sign-out',
    command: () => logout(),
  }
];
</script>

<style scoped>
.navbar-brand {
  cursor: pointer;
}
.custom-hover-white:hover {
  color: white !important;
}
</style>
